import { z } from 'zod';
import { PhoneSchema } from './auth';
import { CountryCodeSchema } from './geo';
import { RunStatementsInputSchema, StatementStatusSchema, type PayoutBatchRow } from './payments';
import { UploadTicketSchema } from './restaurants';

/* ------------------------------------------------------------------ */
/* Riders (W12)                                                        */
/*                                                                     */
/* Onboarding (profile, documents, review, training + quiz, kit),      */
/* going online, 3-hour shifts with a guarantee, trips assigned by Ops */
/* by hand (until W13 dispatch), one earnings ledger, cash on delivery, */
/* daily statements + bank files, Slide-for-SOS and the Capsico        */
/* Suraksha insurance hook.                                            */
/*                                                                     */
/* Money is always in minor units (paise / paisa).                     */
/* ------------------------------------------------------------------ */

const Version = z.number().int().min(1);
const Reason = z.string().trim().min(3, 'Say why (at least 3 characters)').max(300);
const OptionalNote = z.string().trim().max(300).nullable().optional();
const DateText = z.iso.date('Use a date like 2026-10-31');
const Lat = z.number().min(-90).max(90);
const Lng = z.number().min(-180).max(180);

/* ---------------- lifecycle ---------------- */

/**
 * applied      profile + documents being filled in
 * in_review    submitted; Ops check the documents
 * training     approved; watch the videos, pass the quiz, collect the kit
 * active       may go online and take trips
 * rejected     refused for good
 * suspended    blocked by Ops or the daily job (police clearance overdue)
 * offboarded   left Capsico
 */
export const RIDER_STATUSES = [
  'applied',
  'in_review',
  'training',
  'active',
  'rejected',
  'suspended',
  'offboarded',
] as const;
export const RiderStatusSchema = z.enum(RIDER_STATUSES);
export type RiderStatus = z.infer<typeof RiderStatusSchema>;

export const RIDER_STATUS_LABELS: Record<RiderStatus, string> = {
  applied: 'Applying',
  in_review: 'In review',
  training: 'Training',
  active: 'Active',
  rejected: 'Rejected',
  suspended: 'Suspended',
  offboarded: 'Offboarded',
};

export const VEHICLE_CLASSES = ['walker', 'cyclist', 'motorbike'] as const;
export const VehicleClassSchema = z.enum(VEHICLE_CLASSES);
export type VehicleClass = z.infer<typeof VehicleClassSchema>;

export const VEHICLE_CLASS_LABELS: Record<VehicleClass, string> = {
  walker: 'On foot',
  cyclist: 'Bicycle',
  motorbike: 'Motorbike',
};

/* ---------------- documents ---------------- */

export const RIDER_DOC_KINDS = [
  'photo',
  'id_front',
  'id_back',
  'pan',
  'driving_licence',
  'vehicle_rc',
  'vehicle_noc',
  'police_clearance',
  'police_receipt',
] as const;
export const RiderDocKindSchema = z.enum(RIDER_DOC_KINDS);
export type RiderDocKind = z.infer<typeof RiderDocKindSchema>;

export const RIDER_DOC_LABELS: Record<RiderDocKind, string> = {
  photo: 'Your photo (selfie)',
  id_front: 'ID card — front (Aadhaar / citizenship)',
  id_back: 'ID card — back',
  pan: 'PAN card',
  driving_licence: 'Driving licence',
  vehicle_rc: 'Vehicle papers (RC / bluebook)',
  vehicle_noc: 'Owner permission (NOC)',
  police_clearance: 'Police clearance certificate',
  police_receipt: 'Police clearance receipt',
};

/** pending_upload → uploaded → accepted | rejected; a newer upload of the same kind replaces it. */
export const RIDER_DOC_STATUSES = [
  'pending_upload',
  'uploaded',
  'accepted',
  'rejected',
  'replaced',
] as const;
export const RiderDocStatusSchema = z.enum(RIDER_DOC_STATUSES);
export type RiderDocStatus = z.infer<typeof RiderDocStatusSchema>;

export const RIDER_UPLOAD_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
] as const;
export const RIDER_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export const RiderDocumentSchema = z.object({
  id: z.uuid(),
  kind: RiderDocKindSchema,
  label: z.string(),
  status: RiderDocStatusSchema,
  note: z.string().nullable(),
  fileName: z.string(),
  contentType: z.string(),
  uploadedAt: z.string().nullable(),
  reviewedAt: z.string().nullable(),
});
export type RiderDocument = z.infer<typeof RiderDocumentSchema>;

/** One kind the rider must (or may) upload, with what is on file for it. */
export const RequiredDocumentSchema = z.object({
  kind: RiderDocKindSchema,
  label: z.string(),
  required: z.boolean(),
  /** Status of the latest live upload of this kind; null when nothing is on file. */
  status: RiderDocStatusSchema.nullable(),
  note: z.string().nullable(),
});
export type RequiredDocument = z.infer<typeof RequiredDocumentSchema>;

export const StartRiderUploadInputSchema = z.object({
  kind: RiderDocKindSchema,
  fileName: z.string().trim().min(1).max(120),
  contentType: z.enum(RIDER_UPLOAD_TYPES),
  sizeBytes: z.number().int().min(1).max(RIDER_MAX_UPLOAD_BYTES, 'Files can be at most 10 MB'),
});
export type StartRiderUploadInput = z.infer<typeof StartRiderUploadInputSchema>;
export { UploadTicketSchema as RiderUploadTicketSchema };

/* ---------------- profile ---------------- */

export const RiderBankViewSchema = z.object({
  holder: z.string(),
  /** Only the last 4 digits are ever sent. */
  last4: z.string(),
  ifsc: z.string().nullable(),
  bankName: z.string().nullable(),
  branch: z.string().nullable(),
});
export type RiderBankView = z.infer<typeof RiderBankViewSchema>;

export const RiderBankInputSchema = z.object({
  holder: z.string().trim().min(2).max(120),
  /** Leave out to keep the account already on file. Never stored in form drafts. */
  accountNumber: z
    .string()
    .trim()
    .regex(/^[0-9A-Za-z]{6,24}$/, 'Account numbers are 6–24 letters or digits')
    .optional(),
  /** India. */
  ifsc: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'IFSC looks like HDFC0001234')
    .nullable()
    .optional(),
  /** Nepal. */
  bankName: z.string().trim().min(2).max(120).nullable().optional(),
  branch: z.string().trim().min(2).max(120).nullable().optional(),
});
export type RiderBankInput = z.infer<typeof RiderBankInputSchema>;

export const UpdateRiderProfileInputSchema = z.object({
  expectedVersion: Version,
  name: z.string().trim().min(2).max(80),
  townId: z.uuid(),
  zoneId: z.uuid().nullable().optional(),
  vehicleClass: VehicleClassSchema,
  /** Motorbike only. */
  vehicleNumber: z.string().trim().toUpperCase().min(4).max(20).nullable().optional(),
  /** Motorbike: false when riding someone else's bike (owner NOC needed). */
  ownsVehicle: z.boolean().default(true),
  dateOfBirth: DateText,
  emergencyName: z.string().trim().min(2).max(80),
  emergencyPhone: PhoneSchema,
  bank: RiderBankInputSchema.nullable().optional(),
});
export type UpdateRiderProfileInput = z.infer<typeof UpdateRiderProfileInputSchema>;

export const RiderChecklistItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  done: z.boolean(),
});
export type RiderChecklistItem = z.infer<typeof RiderChecklistItemSchema>;

export const CASH_STATES = ['ok', 'warn', 'limit'] as const;
export type CashState = (typeof CASH_STATES)[number];

export const RiderCashSummarySchema = z.object({
  heldMinor: z.number().int(),
  warnMinor: z.number().int(),
  limitMinor: z.number().int(),
  state: z.enum(CASH_STATES),
});
export type RiderCashSummary = z.infer<typeof RiderCashSummarySchema>;

export const RiderKitViewSchema = z.object({
  issuedAt: z.string().nullable(),
  bagId: z.string().nullable(),
  depositMinor: z.number().int(),
  recoveredMinor: z.number().int(),
  returnedAt: z.string().nullable(),
});
export type RiderKitView = z.infer<typeof RiderKitViewSchema>;

export const RiderHelpSchema = z.object({
  /** 112 India / 100 Nepal. */
  emergencyNumber: z.string(),
  /** Capsico Ops line for the rider's country; null when not set. */
  opsPhone: z.string().nullable(),
});
export type RiderHelp = z.infer<typeof RiderHelpSchema>;

export const RiderMeSchema = z.object({
  id: z.uuid(),
  status: RiderStatusSchema,
  name: z.string().nullable(),
  phoneMasked: z.string(),
  country: CountryCodeSchema,
  town: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  zoneId: z.uuid().nullable(),
  vehicleClass: VehicleClassSchema.nullable(),
  vehicleNumber: z.string().nullable(),
  ownsVehicle: z.boolean(),
  dateOfBirth: z.string().nullable(),
  emergencyName: z.string().nullable(),
  emergencyPhone: z.string().nullable(),
  bank: RiderBankViewSchema.nullable(),
  documents: z.array(RiderDocumentSchema),
  requiredDocuments: z.array(RequiredDocumentSchema),
  checklist: z.array(RiderChecklistItemSchema),
  canSubmit: z.boolean(),
  /** Ops notes when the application was sent back. */
  reviewNote: z.string().nullable(),
  rejectReason: z.string().nullable(),
  suspendReason: z.string().nullable(),
  policeClearanceDueAt: z.string().nullable(),
  training: z.object({
    modulesDone: z.number().int(),
    modulesTotal: z.number().int(),
    quizPassed: z.boolean(),
  }),
  kit: RiderKitViewSchema,
  online: z.boolean(),
  onlineSince: z.string().nullable(),
  cash: RiderCashSummarySchema,
  today: z.object({
    businessDate: z.string(),
    trips: z.number().int(),
    earningsMinor: z.number().int(),
  }),
  help: RiderHelpSchema,
  version: z.number().int(),
});
export type RiderMe = z.infer<typeof RiderMeSchema>;

export const ExpectedVersionInputSchema = z.object({ expectedVersion: Version });
export type ExpectedVersionInput = z.infer<typeof ExpectedVersionInputSchema>;

/* ---------------- training + quiz ---------------- */

export const TrainingModuleSchema = z.object({
  key: z.string(),
  title: z.string(),
  videoUrl: z.string(),
  minutes: z.number().int(),
  watched: z.boolean(),
});
export type TrainingModule = z.infer<typeof TrainingModuleSchema>;

export const RiderTrainingSchema = z.object({
  modules: z.array(TrainingModuleSchema),
  quiz: z.object({
    passPercent: z.number().int(),
    passed: z.boolean(),
    lastPercent: z.number().int().nullable(),
    attemptsLeft: z.number().int(),
    /** When the next attempt opens again (after 3 in 24 h). */
    nextAttemptAt: z.string().nullable(),
  }),
});
export type RiderTraining = z.infer<typeof RiderTrainingSchema>;

export const QuizQuestionSchema = z.object({
  id: z.string(),
  question: z.string(),
  options: z.array(z.string()).length(4),
});
export type QuizQuestion = z.infer<typeof QuizQuestionSchema>;

/** The answers are never sent to the app. */
export const RiderQuizSchema = z.object({
  questions: z.array(QuizQuestionSchema),
  passPercent: z.number().int(),
  attemptsLeft: z.number().int(),
});
export type RiderQuiz = z.infer<typeof RiderQuizSchema>;

export const SubmitQuizInputSchema = z.object({
  answers: z
    .array(
      z.object({
        questionId: z.string().min(1).max(40),
        optionIndex: z.number().int().min(0).max(3),
      }),
    )
    .min(1)
    .max(50),
});
export type SubmitQuizInput = z.infer<typeof SubmitQuizInputSchema>;

export const QuizResultSchema = z.object({
  correct: z.number().int(),
  total: z.number().int(),
  percent: z.number().int(),
  passed: z.boolean(),
  attemptsLeft: z.number().int(),
});
export type QuizResult = z.infer<typeof QuizResultSchema>;

/* ---------------- online ---------------- */

export const LocationInputSchema = z.object({
  lat: Lat.optional(),
  lng: Lng.optional(),
});
export type LocationInput = z.infer<typeof LocationInputSchema>;

export const ONLINE_BLOCKS = [
  'not_active',
  'police_clearance_overdue',
  'cash_limit',
  'suspended',
] as const;
export type OnlineBlock = (typeof ONLINE_BLOCKS)[number];

export const RiderOnlineStateSchema = z.object({
  online: z.boolean(),
  since: z.string().nullable(),
  /** Seconds between heartbeats while online. */
  heartbeatSeconds: z.number().int(),
});
export type RiderOnlineState = z.infer<typeof RiderOnlineStateSchema>;

/* ---------------- shifts ---------------- */

export const SLOT_STATUSES = ['open', 'closed', 'settled'] as const;
export const SlotStatusSchema = z.enum(SLOT_STATUSES);
export type SlotStatus = z.infer<typeof SlotStatusSchema>;

export const BOOKING_STATUSES = ['booked', 'cancelled', 'completed', 'no_show'] as const;
export const BookingStatusSchema = z.enum(BOOKING_STATUSES);
export type BookingStatus = z.infer<typeof BookingStatusSchema>;

export const ShiftBookingViewSchema = z.object({
  id: z.uuid(),
  status: BookingStatusSchema,
  onlineMinutes: z.number().int(),
  acceptancePercent: z.number().int().nullable(),
  earningsMinor: z.number().int(),
  topupMinor: z.number().int(),
  guaranteeMet: z.boolean().nullable(),
});
export type ShiftBookingView = z.infer<typeof ShiftBookingViewSchema>;

export const RiderShiftSlotSchema = z.object({
  id: z.uuid(),
  townId: z.uuid(),
  townName: z.string(),
  zoneId: z.uuid().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  capacity: z.number().int(),
  booked: z.number().int(),
  guaranteeMinor: z.number().int(),
  status: SlotStatusSchema,
  booking: ShiftBookingViewSchema.nullable(),
  canBook: z.boolean(),
  canCancel: z.boolean(),
});
export type RiderShiftSlot = z.infer<typeof RiderShiftSlotSchema>;

export const ShiftRulesSchema = z.object({
  minOnlineMinutes: z.number().int(),
  minAcceptancePercent: z.number().int(),
  cancelCutoffMinutes: z.number().int(),
});
export type ShiftRules = z.infer<typeof ShiftRulesSchema>;

export const RiderShiftListSchema = z.object({
  slots: z.array(RiderShiftSlotSchema),
  rules: ShiftRulesSchema,
});
export type RiderShiftList = z.infer<typeof RiderShiftListSchema>;

export const DateRangeQuerySchema = z.object({
  from: DateText.optional(),
  to: DateText.optional(),
});
export type DateRangeQuery = z.infer<typeof DateRangeQuerySchema>;

/* ---------------- trips ---------------- */

/**
 * offered        Ops picked the rider; the app rings (offer window, 30 s default)
 * accepted       rider is on the way to the restaurant
 * at_restaurant  rider arrived
 * picked_up      food is with the rider
 * delivered      done
 * declined / expired / cancelled   the trip ended without a delivery
 */
export const TRIP_STATUSES = [
  'offered',
  'accepted',
  'at_restaurant',
  'picked_up',
  'delivered',
  'declined',
  'expired',
  'cancelled',
] as const;
export const TripStatusSchema = z.enum(TRIP_STATUSES);
export type TripStatus = z.infer<typeof TripStatusSchema>;

export const OPEN_TRIP_STATUSES: readonly TripStatus[] = [
  'offered',
  'accepted',
  'at_restaurant',
  'picked_up',
];

export const TRIP_CANCEL_REASONS = [
  'ops',
  'rider_at_restaurant',
  'order_cancelled',
  'restaurant_cancelled',
] as const;
export const TripCancelReasonSchema = z.enum(TRIP_CANCEL_REASONS);
export type TripCancelReason = z.infer<typeof TripCancelReasonSchema>;

export const RiderTripViewSchema = z.object({
  id: z.uuid(),
  orderId: z.uuid(),
  orderNumber: z.string(),
  status: TripStatusSchema,
  orderStatus: z.string(),
  offeredAt: z.string(),
  offerExpiresAt: z.string().nullable(),
  acceptedAt: z.string().nullable(),
  arrivedAt: z.string().nullable(),
  pickedUpAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  cancelReason: TripCancelReasonSchema.nullable(),
  restaurant: z.object({
    /** Outlet id — riders use it for cash drops at this restaurant. */
    outletId: z.uuid(),
    name: z.string(),
    outletName: z.string(),
    address: z.string(),
    landmark: z.string().nullable(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
  }),
  /** 4-digit pickup passcode the rider reads out at the counter (after accepting). */
  passcode: z.string().nullable(),
  itemCount: z.number().int(),
  customer: z.object({
    name: z.string(),
    area: z.string().nullable(),
    landmark: z.string().nullable(),
    instructions: z.string().nullable(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
  }),
  /** Cash to collect at the door (exact); null when prepaid. */
  codMinor: z.number().int().nullable(),
  tripKm: z.number(),
  sealRecorded: z.boolean(),
  earningsMinor: z.number().int(),
  version: z.number().int(),
  /* W13 dispatch (optional so older app builds keep type-checking; the server always sends them) */
  /** Same id on both trips of a stacked pair; null for a single trip. */
  stackGroupId: z.uuid().nullable().optional(),
  /** 1 = first order of the pair, 2 = the stacked one; null for a single trip. */
  stackSeq: z.number().int().nullable().optional(),
  /** This offer would ride with the trip the rider already has ("+1 stacked"). */
  stacked: z.boolean().optional(),
  /** No-rider pickup bonus on this trip (Capsico-paid). */
  bonusMinor: z.number().int().optional(),
  /** The rider is this restaurant's own rider (paid by the restaurant). */
  ownRestaurant: z.boolean().optional(),
});
export type RiderTripView = z.infer<typeof RiderTripViewSchema>;

export const CurrentTripSchema = z.object({
  /** The trip to show first: a ringing offer, else the earliest open trip. */
  trip: RiderTripViewSchema.nullable(),
  /** W13: every open trip (a stack has two), in step order. */
  trips: z.array(RiderTripViewSchema).optional(),
  stackGroupId: z.uuid().nullable().optional(),
});
export type CurrentTrip = z.infer<typeof CurrentTripSchema>;

export const RiderTripListSchema = z.object({
  date: z.string(),
  trips: z.array(RiderTripViewSchema),
});
export type RiderTripList = z.infer<typeof RiderTripListSchema>;

export const TripDateQuerySchema = z.object({ date: DateText.optional() });
export type TripDateQuery = z.infer<typeof TripDateQuerySchema>;

export const TripStepInputSchema = z.object({ expectedVersion: Version });
export type TripStepInput = z.infer<typeof TripStepInputSchema>;

/**
 * Why a rider declined an offer (W13). vehicle_trouble, too_far and connectivity are valid
 * reasons and do not count toward the decline cool-off; "other" (or no reason) counts.
 */
export const DECLINE_REASONS = ['vehicle_trouble', 'too_far', 'connectivity', 'other'] as const;
export const DeclineReasonSchema = z.enum(DECLINE_REASONS);
export type DeclineReason = z.infer<typeof DeclineReasonSchema>;
export const VALID_DECLINE_REASONS: readonly DeclineReason[] = [
  'vehicle_trouble',
  'too_far',
  'connectivity',
];
export const DECLINE_REASON_LABELS: Record<DeclineReason, string> = {
  vehicle_trouble: 'Vehicle trouble',
  too_far: 'Too far',
  connectivity: 'Phone / network trouble',
  other: 'Other',
};

export const DeclineTripInputSchema = z.object({
  expectedVersion: Version,
  reason: DeclineReasonSchema.optional(),
});
export type DeclineTripInput = z.infer<typeof DeclineTripInputSchema>;

/** Decline cool-off state for the Rider app banner (W13). */
export const RiderCooloffSchema = z.object({
  coolingOff: z.boolean(),
  until: z.string().nullable(),
  secondsLeft: z.number().int(),
  /** Declines / expiries counted in the recent window (valid reasons excluded). */
  counted: z.number().int(),
  windowOffers: z.number().int(),
  /** This many counted declines start a cool-off. */
  limit: z.number().int(),
  /** The day's milestone bonus is lost (too many declines today). */
  milestoneForfeited: z.boolean(),
});
export type RiderCooloff = z.infer<typeof RiderCooloffSchema>;

export const PickUpInputSchema = z.object({
  expectedVersion: Version,
  /** The seal code on the bag (must match the one the kitchen scanned, when it scanned one). */
  sealCode: z.string().trim().toUpperCase().min(4).max(20).nullable().optional(),
});
export type PickUpInput = z.infer<typeof PickUpInputSchema>;

export const DeliverInputSchema = z.object({
  expectedVersion: Version,
  /** Cash on delivery: the exact amount collected (must equal the amount due). */
  cashCollectedMinor: z.number().int().min(0).max(10_000_000).nullable().optional(),
  /* W14 delivery proof (optional so W12/W13 app builds keep working while tracking is off) */
  /** The customer's 4-digit delivery code, when the order needs one. */
  code: z
    .string()
    .trim()
    .regex(/^[0-9]{4}$/, 'Enter the 4-digit code')
    .nullable()
    .optional(),
  /** Leave at door: the key of the uploaded door photo (from …/proof-photo). */
  photoKey: z.string().trim().min(8).max(200).nullable().optional(),
  /** The phone's position when Deliver was pressed (the location gate). */
  location: z
    .object({
      lat: Lat,
      lng: Lng,
      accuracyM: z.number().min(0).max(10_000),
    })
    .nullable()
    .optional(),
});
export type DeliverInput = z.infer<typeof DeliverInputSchema>;

export const RiderCancelTripInputSchema = z.object({
  expectedVersion: Version,
  reason: Reason,
});
export type RiderCancelTripInput = z.infer<typeof RiderCancelTripInputSchema>;

/* ---------------- earnings ---------------- */

export const EARNING_KINDS = [
  'base',
  'distance',
  'wait',
  'rain',
  'festival',
  'tip',
  'dry_run',
  'penalty',
  'milestone',
  'guarantee',
  'kit_deposit',
  'kit_refund',
  'adjustment',
] as const;
export const EarningKindSchema = z.enum(EARNING_KINDS);
export type EarningKind = z.infer<typeof EarningKindSchema>;

export const EARNING_KIND_LABELS: Record<EarningKind, string> = {
  base: 'Trip pay',
  distance: 'Distance pay',
  wait: 'Waiting at restaurant',
  rain: 'Rain bonus',
  festival: 'Festival bonus',
  tip: 'Customer tip',
  dry_run: 'Dry run (restaurant cancelled)',
  penalty: 'Penalty',
  milestone: 'Daily milestone',
  guarantee: 'Shift guarantee top-up',
  kit_deposit: 'Kit deposit',
  kit_refund: 'Kit deposit refund',
  adjustment: 'Adjustment',
};

/**
 * W13 ledger kinds: the stacked-order bonus and the no-rider pickup bonus. They are stored as
 * their own kinds; on the wire an earnings line keeps `kind: 'adjustment'` (so app builds that
 * only know the W12 kinds keep working) and carries the real kind in `dispatchKind`.
 */
export const DISPATCH_EARNING_KINDS = ['stack', 'pickup_bonus'] as const;
export const DispatchEarningKindSchema = z.enum(DISPATCH_EARNING_KINDS);
export type DispatchEarningKind = z.infer<typeof DispatchEarningKindSchema>;
export const DISPATCH_EARNING_KIND_LABELS: Record<DispatchEarningKind, string> = {
  stack: 'Stacked order bonus',
  pickup_bonus: 'Pickup bonus',
};
/** Every kind the earnings ledger stores. */
export type StoredEarningKind = EarningKind | DispatchEarningKind;

export const EarningLineSchema = z.object({
  id: z.uuid(),
  kind: EarningKindSchema,
  amountMinor: z.number().int(),
  businessDate: z.string(),
  tripId: z.uuid().nullable(),
  orderNumber: z.string().nullable(),
  note: z.string().nullable(),
  chargedTo: z.enum(['capsico', 'restaurant']),
  statementId: z.uuid().nullable(),
  createdAt: z.string(),
  /** W13: the real kind of a dispatch line (kind is then "adjustment"); null otherwise. */
  dispatchKind: DispatchEarningKindSchema.nullable().optional(),
  /** W13: "restaurant" for own-rider lines (paid by the restaurant, not on Capsico statements). */
  paidBy: z.enum(['capsico', 'restaurant']).optional(),
});
export type EarningLine = z.infer<typeof EarningLineSchema>;

export const RiderEarningsSchema = z.object({
  from: z.string(),
  to: z.string(),
  totalMinor: z.number().int(),
  days: z.array(
    z.object({ date: z.string(), totalMinor: z.number().int(), trips: z.number().int() }),
  ),
  lines: z.array(EarningLineSchema),
});
export type RiderEarnings = z.infer<typeof RiderEarningsSchema>;

/* ---------------- statements + payouts ---------------- */

export const RiderStatementRowSchema = z.object({
  id: z.uuid(),
  riderId: z.uuid(),
  riderName: z.string().nullable(),
  phoneMasked: z.string(),
  country: CountryCodeSchema,
  businessDate: z.string(),
  status: StatementStatusSchema,
  earningsMinor: z.number().int(),
  kitRecoveryMinor: z.number().int(),
  cashNettedMinor: z.number().int(),
  carriedInMinor: z.number().int(),
  netMinor: z.number().int(),
  batchId: z.uuid().nullable(),
  paidAt: z.string().nullable(),
  paidReference: z.string().nullable(),
  holdReason: z.string().nullable(),
  bankLast4: z.string().nullable(),
  bankReady: z.boolean(),
  version: z.number().int(),
});
export type RiderStatementRow = z.infer<typeof RiderStatementRowSchema>;

export const RiderStatementDetailSchema = RiderStatementRowSchema.extend({
  lines: z.array(EarningLineSchema),
});
export type RiderStatementDetail = z.infer<typeof RiderStatementDetailSchema>;

export const RiderStatementListSchema = z.object({ rows: z.array(RiderStatementRowSchema) });
export type RiderStatementList = z.infer<typeof RiderStatementListSchema>;

export const RiderPayoutQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  status: StatementStatusSchema.optional(),
  date: DateText.optional(),
  search: z.string().trim().max(40).optional(),
});
export type RiderPayoutQuery = z.infer<typeof RiderPayoutQuerySchema>;

export const RiderPayoutListSchema = z.object({
  rows: z.array(RiderStatementRowSchema),
  counts: z.record(StatementStatusSchema, z.number().int()),
  totals: z.object({ readyMinor: z.number().int(), heldMinor: z.number().int() }),
});
export type RiderPayoutList = z.infer<typeof RiderPayoutListSchema>;

export type RiderBatchList = { rows: PayoutBatchRow[] };
export { RunStatementsInputSchema as RunRiderStatementsInputSchema };

/* ---------------- cash ---------------- */

export const CASH_KINDS = [
  'cod_collected',
  'cash_drop',
  'hub_handin',
  'statement_netting',
  'adjustment',
] as const;
export const CashKindSchema = z.enum(CASH_KINDS);
export type CashKind = z.infer<typeof CashKindSchema>;

export const CASH_STATUSES = ['pending', 'confirmed', 'rejected'] as const;
export const CashStatusSchema = z.enum(CASH_STATUSES);
export type CashStatus = z.infer<typeof CashStatusSchema>;

export const CashEntrySchema = z.object({
  id: z.uuid(),
  kind: CashKindSchema,
  /** Signed: collections add, drops / hand-ins / netting take away. */
  amountMinor: z.number().int(),
  status: CashStatusSchema,
  orderNumber: z.string().nullable(),
  outletName: z.string().nullable(),
  note: z.string().nullable(),
  hasSlip: z.boolean(),
  createdAt: z.string(),
  confirmedAt: z.string().nullable(),
});
export type CashEntry = z.infer<typeof CashEntrySchema>;

export const RiderCashSchema = RiderCashSummarySchema.extend({
  entries: z.array(CashEntrySchema),
});
export type RiderCash = z.infer<typeof RiderCashSchema>;

export const CashDropInputSchema = z.object({
  amountMinor: z.number().int().min(100).max(10_000_000),
  /** The partner restaurant where the cash was dropped. */
  outletId: z.uuid(),
  note: OptionalNote,
});
export type CashDropInput = z.infer<typeof CashDropInputSchema>;

export const CashHandinInputSchema = z.object({
  amountMinor: z.number().int().min(100).max(10_000_000),
  note: OptionalNote,
  /** The bank slip photo; the response carries a PUT link for it. */
  slip: z
    .object({
      fileName: z.string().trim().min(1).max(120),
      contentType: z.enum(RIDER_UPLOAD_TYPES),
      sizeBytes: z.number().int().min(1).max(RIDER_MAX_UPLOAD_BYTES),
    })
    .nullable()
    .optional(),
});
export type CashHandinInput = z.infer<typeof CashHandinInputSchema>;

export const CashHandinResultSchema = z.object({
  entry: CashEntrySchema,
  upload: UploadTicketSchema.nullable(),
});
export type CashHandinResult = z.infer<typeof CashHandinResultSchema>;

/* ---------------- SOS ---------------- */

export const SOS_STATUSES = ['open', 'acknowledged', 'resolved'] as const;
export const SosStatusSchema = z.enum(SOS_STATUSES);
export type SosStatus = z.infer<typeof SosStatusSchema>;

export const SosInputSchema = z.object({
  lat: Lat.nullable().optional(),
  lng: Lng.nullable().optional(),
  tripId: z.uuid().nullable().optional(),
  note: OptionalNote,
});
export type SosInput = z.infer<typeof SosInputSchema>;

export const RiderSosSchema = z.object({
  id: z.uuid(),
  status: SosStatusSchema,
  tripId: z.uuid().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
  acknowledgedAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
});
export type RiderSos = z.infer<typeof RiderSosSchema>;

export const CurrentSosSchema = z.object({
  sos: RiderSosSchema.nullable(),
  help: RiderHelpSchema,
});
export type CurrentSos = z.infer<typeof CurrentSosSchema>;

/* ---------------- push ---------------- */

export const RiderPushDeviceInputSchema = z.object({
  token: z.string().trim().min(20).max(4096),
  platform: z.enum(['android', 'web']),
});
export type RiderPushDeviceInput = z.infer<typeof RiderPushDeviceInputSchema>;

/* ================================================================== */
/* Capsico OS                                                          */
/* ================================================================== */

export const RIDER_LIST_VIEWS = ['all', 'review'] as const;

export const RiderListQuerySchema = z.object({
  view: z.enum(RIDER_LIST_VIEWS).default('all'),
  status: RiderStatusSchema.optional(),
  townId: z.uuid().optional(),
  vehicleClass: VehicleClassSchema.optional(),
  search: z.string().trim().max(40).optional(),
});
export type RiderListQuery = z.infer<typeof RiderListQuerySchema>;

export const RiderRowSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  phoneMasked: z.string(),
  country: CountryCodeSchema,
  townName: z.string().nullable(),
  vehicleClass: VehicleClassSchema.nullable(),
  status: RiderStatusSchema,
  online: z.boolean(),
  cashHeldMinor: z.number().int(),
  submittedAt: z.string().nullable(),
  policeClearanceDueAt: z.string().nullable(),
  createdAt: z.string(),
  version: z.number().int(),
});
export type RiderRow = z.infer<typeof RiderRowSchema>;

export const RiderListSchema = z.object({
  rows: z.array(RiderRowSchema),
  counts: z.record(RiderStatusSchema, z.number().int()),
});
export type RiderList = z.infer<typeof RiderListSchema>;

export const OpsRiderDocumentSchema = RiderDocumentSchema.extend({
  /** Short-lived signed link to view the file (private bucket). */
  viewUrl: z.string().nullable(),
  version: z.number().int(),
});
export type OpsRiderDocument = z.infer<typeof OpsRiderDocumentSchema>;

export const RiderEventSchema = z.object({
  id: z.string(),
  action: z.string(),
  detail: z.record(z.string(), z.unknown()),
  actorName: z.string().nullable(),
  at: z.string(),
});
export type RiderEvent = z.infer<typeof RiderEventSchema>;

export const RiderDetailSchema = RiderRowSchema.extend({
  townId: z.uuid().nullable(),
  zoneId: z.uuid().nullable(),
  vehicleNumber: z.string().nullable(),
  ownsVehicle: z.boolean(),
  dateOfBirth: z.string().nullable(),
  emergencyName: z.string().nullable(),
  emergencyPhone: z.string().nullable(),
  bank: RiderBankViewSchema.nullable(),
  documents: z.array(OpsRiderDocumentSchema),
  requiredDocuments: z.array(RequiredDocumentSchema),
  checklist: z.array(RiderChecklistItemSchema),
  reviewNote: z.string().nullable(),
  rejectReason: z.string().nullable(),
  suspendReason: z.string().nullable(),
  approvedAt: z.string().nullable(),
  activatedAt: z.string().nullable(),
  training: z.object({
    modulesDone: z.number().int(),
    modulesTotal: z.number().int(),
    quizPassed: z.boolean(),
    attempts: z.number().int(),
    lastPercent: z.number().int().nullable(),
  }),
  kit: RiderKitViewSchema,
  cash: RiderCashSchema,
  bookings: z.array(
    ShiftBookingViewSchema.extend({ slotId: z.uuid(), startsAt: z.string(), endsAt: z.string() }),
  ),
  trips: z.array(RiderTripViewSchema),
  earnings: z.array(EarningLineSchema),
  statements: z.array(RiderStatementRowSchema),
  events: z.array(RiderEventSchema),
});
export type RiderDetail = z.infer<typeof RiderDetailSchema>;

export const ReviewRiderDocumentInputSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('accept'), note: OptionalNote }),
  z.object({ decision: z.literal('reject'), note: Reason }),
]);
export type ReviewRiderDocumentInput = z.infer<typeof ReviewRiderDocumentInputSchema>;

export const RiderReasonInputSchema = z.object({ expectedVersion: Version, reason: Reason });
export type RiderReasonInput = z.infer<typeof RiderReasonInputSchema>;

export const OffboardRiderInputSchema = z.object({
  expectedVersion: Version,
  reason: Reason,
  /** Bag and shirts returned: the recovered kit deposit is refunded on the next statement. */
  kitReturned: z.boolean(),
});
export type OffboardRiderInput = z.infer<typeof OffboardRiderInputSchema>;

export const RiderKitInputSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('issue'),
    expectedVersion: Version,
    bagId: z.string().trim().min(1).max(40).nullable().optional(),
  }),
  z.object({ action: z.literal('returned'), expectedVersion: Version }),
]);
export type RiderKitInput = z.infer<typeof RiderKitInputSchema>;

export const RiderAdjustmentInputSchema = z.object({
  /** Signed, never zero. */
  amountMinor: z
    .number()
    .int()
    .min(-10_000_000)
    .max(10_000_000)
    .refine((value) => value !== 0, 'Enter an amount'),
  reason: Reason,
});
export type RiderAdjustmentInput = z.infer<typeof RiderAdjustmentInputSchema>;

/* ---------------- shifts (Ops) ---------------- */

export const OpsShiftQuerySchema = z.object({
  date: DateText,
  townId: z.uuid().optional(),
});
export type OpsShiftQuery = z.infer<typeof OpsShiftQuerySchema>;

export const OpsShiftSlotSchema = z.object({
  id: z.uuid(),
  townId: z.uuid(),
  townName: z.string(),
  zoneId: z.uuid().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  capacity: z.number().int(),
  booked: z.number().int(),
  guaranteeMinor: z.number().int(),
  status: SlotStatusSchema,
  riders: z.array(
    z.object({
      riderId: z.uuid(),
      name: z.string().nullable(),
      bookingStatus: BookingStatusSchema,
      onlineMinutes: z.number().int(),
      topupMinor: z.number().int(),
    }),
  ),
});
export type OpsShiftSlot = z.infer<typeof OpsShiftSlotSchema>;

export const OpsShiftListSchema = z.object({ slots: z.array(OpsShiftSlotSchema) });
export type OpsShiftList = z.infer<typeof OpsShiftListSchema>;

export const CreateShiftSlotsInputSchema = z.object({
  townId: z.uuid(),
  zoneId: z.uuid().nullable().optional(),
  date: DateText,
  /** Local start times "HH:MM"; one for a single slot, several to bulk-create the day. */
  startTimes: z
    .array(z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a time like 09:00'))
    .min(1)
    .max(24),
  /** Slot length in hours; defaults to riders.slot_hours. */
  hours: z.number().int().min(1).max(12).nullable().optional(),
  capacity: z.number().int().min(1).max(500),
  /** Defaults to riders.slot_guarantee for the town. */
  guaranteeMinor: z.number().int().min(0).max(10_000_000).nullable().optional(),
});
export type CreateShiftSlotsInput = z.infer<typeof CreateShiftSlotsInputSchema>;

export const CloseShiftInputSchema = z.object({ reason: Reason });
export type CloseShiftInput = z.infer<typeof CloseShiftInputSchema>;

/* ---------------- manual dispatch (Ops) ---------------- */

export const AvailableRidersQuerySchema = z.object({ orderId: z.uuid() });
export type AvailableRidersQuery = z.infer<typeof AvailableRidersQuerySchema>;

export const AVAILABILITY_HIDE_REASONS = [
  'too_far_for_vehicle',
  'cash_limit',
  'cod_in_a_row',
  'on_a_trip',
] as const;
export type AvailabilityHideReason = (typeof AVAILABILITY_HIDE_REASONS)[number];

export const AvailableRiderSchema = z.object({
  riderId: z.uuid(),
  name: z.string().nullable(),
  phoneMasked: z.string(),
  vehicleClass: VehicleClassSchema,
  /** Straight-line km from the restaurant; null when the rider's location is unknown. */
  distanceKm: z.number().nullable(),
  cashHeldMinor: z.number().int(),
  tripsToday: z.number().int(),
});
export type AvailableRider = z.infer<typeof AvailableRiderSchema>;

export const AvailableRidersSchema = z.object({
  orderId: z.uuid(),
  orderNumber: z.string(),
  tripKm: z.number(),
  codMinor: z.number().int().nullable(),
  riders: z.array(AvailableRiderSchema),
  hidden: z.array(
    AvailableRiderSchema.extend({
      reason: z.enum(AVAILABILITY_HIDE_REASONS),
    }),
  ),
  offerSeconds: z.number().int(),
});
export type AvailableRiders = z.infer<typeof AvailableRidersSchema>;

export const AssignRiderInputSchema = z.object({ orderId: z.uuid(), riderId: z.uuid() });
export type AssignRiderInput = z.infer<typeof AssignRiderInputSchema>;

export const UnassignTripInputSchema = z.object({ reason: Reason });
export type UnassignTripInput = z.infer<typeof UnassignTripInputSchema>;

export const OpsTripSchema = RiderTripViewSchema.extend({
  riderId: z.uuid(),
  riderName: z.string().nullable(),
  riderPhoneMasked: z.string(),
});
export type OpsTrip = z.infer<typeof OpsTripSchema>;

export const OrderTripSchema = z.object({ trip: OpsTripSchema.nullable() });
export type OrderTrip = z.infer<typeof OrderTripSchema>;

/* ---------------- cash (Ops) ---------------- */

export const OpsCashEntrySchema = CashEntrySchema.extend({
  riderId: z.uuid(),
  riderName: z.string().nullable(),
  /** Short-lived link to the bank slip photo. */
  slipUrl: z.string().nullable(),
});
export type OpsCashEntry = z.infer<typeof OpsCashEntrySchema>;

export const OpsCashListSchema = z.object({ rows: z.array(OpsCashEntrySchema) });
export type OpsCashList = z.infer<typeof OpsCashListSchema>;

export const CashDecisionInputSchema = z.object({ note: OptionalNote });
export type CashDecisionInput = z.infer<typeof CashDecisionInputSchema>;

/* ---------------- SOS (Ops) ---------------- */

export const OpsSosQuerySchema = z.object({ status: SosStatusSchema.optional() });
export type OpsSosQuery = z.infer<typeof OpsSosQuerySchema>;

export const OpsSosSchema = RiderSosSchema.extend({
  riderId: z.uuid(),
  riderName: z.string().nullable(),
  riderPhoneMasked: z.string(),
  orderId: z.uuid().nullable(),
  orderNumber: z.string().nullable(),
  opsNotes: z.string().nullable(),
  version: z.number().int(),
});
export type OpsSos = z.infer<typeof OpsSosSchema>;

export const OpsSosListSchema = z.object({
  rows: z.array(OpsSosSchema),
  open: z.number().int(),
});
export type OpsSosList = z.infer<typeof OpsSosListSchema>;

export const SosActionInputSchema = z.object({ note: OptionalNote });
export type SosActionInput = z.infer<typeof SosActionInputSchema>;

/* ---------------- insurance (Ops) ---------------- */

export const CLAIM_KINDS = ['accident', 'hospital', 'death_disability'] as const;
export const ClaimKindSchema = z.enum(CLAIM_KINDS);
export type ClaimKind = z.infer<typeof ClaimKindSchema>;

export const CLAIM_STATUSES = ['reported', 'submitted', 'settled', 'rejected'] as const;
export const ClaimStatusSchema = z.enum(CLAIM_STATUSES);
export type ClaimStatus = z.infer<typeof ClaimStatusSchema>;

export const PremiumQuerySchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use a month like 2026-10'),
  country: CountryCodeSchema.optional(),
});
export type PremiumQuery = z.infer<typeof PremiumQuerySchema>;

export const PremiumSummarySchema = z.object({
  month: z.string(),
  rows: z.array(
    z.object({
      riderId: z.uuid(),
      riderName: z.string().nullable(),
      country: CountryCodeSchema,
      trips: z.number().int(),
      premiumMinor: z.number().int(),
    }),
  ),
  totals: z.array(
    z.object({
      country: CountryCodeSchema,
      trips: z.number().int(),
      premiumMinor: z.number().int(),
    }),
  ),
});
export type PremiumSummary = z.infer<typeof PremiumSummarySchema>;

export const ClaimSchema = z.object({
  id: z.uuid(),
  riderId: z.uuid(),
  riderName: z.string().nullable(),
  kind: ClaimKindSchema,
  status: ClaimStatusSchema,
  amountClaimedMinor: z.number().int().nullable(),
  amountSettledMinor: z.number().int().nullable(),
  notes: z.string().nullable(),
  sosId: z.uuid().nullable(),
  tripId: z.uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number().int(),
});
export type Claim = z.infer<typeof ClaimSchema>;

export const ClaimListSchema = z.object({ rows: z.array(ClaimSchema) });
export type ClaimList = z.infer<typeof ClaimListSchema>;

export const ClaimQuerySchema = z.object({ status: ClaimStatusSchema.optional() });
export type ClaimQuery = z.infer<typeof ClaimQuerySchema>;

export const CreateClaimInputSchema = z.object({
  riderId: z.uuid(),
  kind: ClaimKindSchema,
  amountClaimedMinor: z.number().int().min(0).max(1_000_000_000).nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
  sosId: z.uuid().nullable().optional(),
  tripId: z.uuid().nullable().optional(),
});
export type CreateClaimInput = z.infer<typeof CreateClaimInputSchema>;

export const ClaimStatusInputSchema = z.object({
  expectedVersion: Version,
  status: ClaimStatusSchema,
  amountSettledMinor: z.number().int().min(0).max(1_000_000_000).nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
});
export type ClaimStatusInput = z.infer<typeof ClaimStatusInputSchema>;

/* ---------------- jobs ---------------- */

export const RiderSweepResultSchema = z.object({
  offersExpired: z.number().int(),
  sessionsClosed: z.number().int(),
  slotsSettled: z.number().int(),
  topups: z.number().int(),
  noShows: z.number().int(),
});
export type RiderSweepResult = z.infer<typeof RiderSweepResultSchema>;

export const RiderDailyResultSchema = z.object({ suspended: z.number().int() });
export type RiderDailyResult = z.infer<typeof RiderDailyResultSchema>;

/* ---------------- helpers ---------------- */

/** Emergency number per country (police / ambulance). */
export const EMERGENCY_NUMBERS = { IN: '112', NP: '100' } as const;

/** Rider heartbeat interval while online. */
export const RIDER_HEARTBEAT_SECONDS = 60;
